import { IGroup } from "@app/main/components/resourceManagement/groups/_models/IGroup";
import { IGroupWithIds } from "@app/main/components/resourceManagement/groups/_models/IGroupWithIds";
import { GeofenceType } from "@enums/GeofenceType";
import { UserStatus } from "@enums/UserType";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IDriver } from "@interfaces/IDriver";
import { GeofenceCategory, IGeofence } from "@interfaces/IGeofences";
import { IInputType } from "@interfaces/IInputType";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { DriverService } from "@services/driver.service";
import { GeofenceService } from "@services/geofence.service";
import { GroupService } from "@services/group.service.ajs";
import { InputTypeService } from "@services/inputType.service";
import { IAutocompleteResponse, MapzenService } from "@services/mapzen.service";
import { SessionService } from "@services/session.service";
import { TranslationService } from "@services/translation.service";
import { UsersService } from "@services/users.service";
import { VehicleService } from "@services/vehicle.service";
import * as angular from "angular";
import { SettingsName } from "src/app/class/SettingsName";
import "./autocomplete.scss";

export interface IAutocompleteMatch<T = any> {
    label: string;
    icon: string;
    source?: string;
    location?: google.maps.LatLngLiteral;

    objectId?: number;
    object?: T;
}

interface IAutocompleteComponentBindings {
    includeDrivers: boolean;
    includeGeofences: boolean;
    includeZones: boolean;
    includePlaces: boolean;
    includeVehicles: boolean;
    includeUsers: boolean;
    includeDepots: boolean;
    includeInputTypes: boolean;

    includeVehicleLatestPlot: boolean;

    isRequired: boolean;
    inputName: string;

    filterByGroupId: number;
    disabled: boolean;
    eagerLoad: boolean;

    selectedMatch: any;
    selectedMatchId: number;
    querySource: (args: { $searchText: string }) => angular.IPromise<IAutocompleteMatch[]>;
    querySourceLabel: string;

    label: string;
    placeHolderText: string;
    customPlaceHolderText: string;
    maxMatches: number;
    searchOnClick: boolean;
    onSelect: (args: { $match: IAutocompleteMatch }) => void;
    onSearchTextChanged: (args: { text: string }) => void;
    onClear: () => void;

}

export class AutocompleteComponentController implements angular.IComponentController, IAutocompleteComponentBindings {
    externallyUpdated = false;
    label = "";
    searchText: string;

    includeDrivers: boolean;
    includeGeofences: boolean;
    includeZones: boolean;
    includePlaces: boolean;
    includeVehicles: boolean;
    includeGroups: boolean;
    includeUsers: boolean;
    includeDepots: boolean;
    includeInputTypes: boolean;

    includeVehicleLatestPlot: boolean;

    isRequired: boolean = false;
    inputName: string;

    filterByGroupId: number;
    disabled: boolean;
    eagerLoad: boolean;

    loading: boolean;

    searchOnClick: boolean;

    onSelect: (args: { $match: IAutocompleteMatch }) => void;
    onSearchTextChanged: (args: { text: string }) => void;
    onClear: () => void;


    placeHolderText = "";
    customPlaceHolderText = "";
    maxMatches: number;

    selectedMatch: any;
    selectedMatchId: number;
    querySource: (args: { $searchText: string }) => angular.IPromise<IAutocompleteMatch[]>;
    querySourceLabel: string;

    matches: IAutocompleteMatch[] = [];

    geofences: IGeofence[];
    zones: IGeofence[];
    vehicles: IVehicle[];
    drivers: IDriver[];
    groups: IGroupWithIds[];
    users: IUser[];
    depots: IGroup[];
    inputTypes: IInputType[];

    countryCode: string;

    lastSelectedItem: number;
    lastSelectedSource: string;

    dataLoadedPromise: angular.IPromise<any>;

    static $inject = ["$element", "$q", "$timeout", "geofenceService", "mapzenService", "vehicleService", "driverService", "groupService", "usersService", "sessionService", "translationService", "inputTypeService"];

    constructor(
        private readonly $element: JQLite,
        private readonly $q: angular.IQService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly geofenceService: GeofenceService,
        private readonly mapzenService: MapzenService,
        private readonly vehicleService: VehicleService,
        private readonly driverService: DriverService,
        private readonly groupService: GroupService,
        private readonly usersService: UsersService,
        private readonly sessionService: SessionService,
        private readonly translationService: TranslationService,
        private readonly inputTypeService: InputTypeService
    ) { }

    $onInit() {
        this.removeBrowserAutocomplete();

        this.lastSelectedItem = -1;
        this.lastSelectedSource = "";

        if (!this.customPlaceHolderText?.length) {
            this.placeHolderText = this.translationService.t('autocomplete.searchInitial');
        }

        if (!this.maxMatches) {
            this.maxMatches = 0;
        }

        //if ((this.selectedMatchId && this.selectedMatchId > 0) || this.eagerLoad) {
        //    this.getData();
        //}

        this.buildPlaceHolderText();
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj["selectedMatchId"] && onChangesObj["selectedMatch"]) {
            console.warn("Using both 'selectedMatchId' and 'selectedMatch' bindings at the same time can lead to unexpected behavior")
        }

        const labelChange = onChangesObj["label"];

        if (this.selectedMatchId === null) {
            this.clear();

            return;
        }

        if (labelChange) {
            if (!labelChange.isFirstChange() || (labelChange.currentValue && labelChange.currentValue.length > 0)) {
                this.searchText = labelChange.currentValue;
                this.label = labelChange.currentValue;
                this.externallyUpdated = true;
            }
        }

        if (onChangesObj["selectedMatchId"] && this.selectedMatchId) {
            this.getData().then(() => {
                this.setSelectedMatchById(onChangesObj["selectedMatchId"].currentValue);
            });
        }

        if (this.querySource) {
            return;
        }

        if (onChangesObj["filterByGroupId"] && this.dataLoadedPromise) {
            this.getData(true);
        }
    }

    onSearchTextChange(): void {
        if (this.onSearchTextChanged) {
            this.onSearchTextChanged({ text: this.searchText });
        }

        if (!this.searchText?.length && this.onClear) {
            this.clear();
        }
    }

    onSelectedItemChange(match: IAutocompleteMatch) {
        if (match) {
            this.selectedMatchId = match.objectId;
        }
        if (this.onSelect && !this.externallyUpdated && this.uniqueCallback(match)) {
            this.onSelect({ $match: match });
        }

        this.externallyUpdated = false;
    }

    querySearch(searchText: string): angular.IPromise<any> {
        if (typeof searchText === "undefined" || searchText === null) {
            searchText = "";
        }

        this.matches = [];

        const lowercaseSearchText = searchText.toLowerCase();

        const dataUpdatePromise = this.getData();
        const mapzenAutocompletePromise = this.includePlaces ? this.mapzenService.autocomplete(searchText, this.countryCode) : this.$q.when({ results: [] });
        const querySourcePromise = this.querySource ? this.querySource({ $searchText: searchText }) : this.$q.when([]);

        return this.$q
            .all([dataUpdatePromise, mapzenAutocompletePromise, querySourcePromise])
            .then(([dataResults, placeResults, querySourceResults]) => {

                const driverMatches = this.findDriverMatches(lowercaseSearchText);
                const geofenceMatches = this.findGeofenceMatches(lowercaseSearchText);
                const zoneMatches = this.findZoneMatches(lowercaseSearchText);
                const vehicleMatches = this.findVehicleMatches(lowercaseSearchText);
                const groupMatches = this.findGroupMatches(lowercaseSearchText);
                const userMatches = this.findUserMatches(lowercaseSearchText);
                const depotMatches = this.findDepotMatches(lowercaseSearchText);
                const inputTypeMatches = this.findInputTypeMatches(lowercaseSearchText);
                const placeMatches = this.findMapzenPlaceMatches(lowercaseSearchText, placeResults);
                const querySourceMatches = this.findQuerySourceMatches(lowercaseSearchText, querySourceResults);

                this.populateMatches([
                    driverMatches,
                    geofenceMatches,
                    zoneMatches,
                    vehicleMatches,
                    groupMatches,
                    userMatches,
                    depotMatches,
                    inputTypeMatches,
                    placeMatches,
                    querySourceMatches]);

                return this.matches.sort((a: IAutocompleteMatch, b: IAutocompleteMatch) => {
                    return a.label.localeCompare(b.label);
                });
            });
    }

    private removeBrowserAutocomplete(): void {
        this.$timeout(() => {
            const inputElement = this.$element.find("input")[0];

            if (!inputElement) {
                return;
            }

            inputElement.setAttribute("name", `notDefaultBrowserAutoComplete-${Date.now()}`);
        }, 10);
    }

    private clear(): void {
        this.searchText = null;
        this.selectedMatch = null;
        this.selectedMatchId = null;
        this.externallyUpdated = false;

        if (this.onClear) {
            this.onClear();
        }
    }

    private uniqueCallback(match: IAutocompleteMatch): boolean {
        if (!match) {
            this.lastSelectedItem = -1;
            this.lastSelectedSource = "";
            return true;
        }

        const isUnique = this.lastSelectedItem !== match.objectId || this.lastSelectedSource !== match.source;

        this.lastSelectedItem = match.objectId;
        this.lastSelectedSource = match.source;

        return isUnique;
    }

    private buildPlaceHolderText() {

        if (!this.customPlaceHolderText || this.customPlaceHolderText.length === 0) {

            this.placeHolderText = this.translationService.t('autocomplete.search');
            const whatsIncluded = this.getWhatsIncluded();

            // To build the appropriate text e.g. 
            // - 'Search geofences'
            // - 'Search places, geofences or vehicles'
            for (let i = 0; i < whatsIncluded.length; i++) {
                if (i === 0) {
                    this.placeHolderText += ` ${whatsIncluded[i]}`;
                }
                else {
                    if (whatsIncluded.length - (i + 1) === 0) {
                        this.placeHolderText += ` or ${whatsIncluded[i]}`;
                    }
                    else {
                        this.placeHolderText += `, ${whatsIncluded[i]}`;
                    }
                }
            }
        }
        else {
            this.placeHolderText = this.customPlaceHolderText;
        }

    }

    private getWhatsIncluded(includeNumbers = true): string[] {

        const whatsIncluded: string[] = [];

        includeNumbers = false;

        if (this.includePlaces) {
            whatsIncluded.push(this.translationService.t('autocomplete.places'));
        }

        if (this.includeDrivers) {
            let label = this.translationService.t('autocomplete.drivers');

            if (includeNumbers && this.drivers) {
                label += ` (${this.drivers.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.includeGeofences) {
            let label = this.translationService.t('autocomplete.geofences');

            if (includeNumbers && this.geofences) {
                label += ` (${this.geofences.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.includeZones) {
            let label = this.translationService.t('autocomplete.zones');

            if (includeNumbers && this.zones) {
                label += ` (${this.zones.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.includeVehicles) {
            let label = this.translationService.t('autocomplete.vehicles');

            if (includeNumbers && this.vehicles) {
                label += ` (${this.vehicles.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.includeGroups) {
            let label = this.translationService.t('autocomplete.groups');
            if (includeNumbers && this.groups) {
                label += ` (${this.groups.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.includeUsers) {
            let label = this.translationService.t('autocomplete.users');

            if (includeNumbers && this.users) {
                label += ` (${this.users.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.includeDepots) {
            let label = this.translationService.t('autocomplete.depots');

            if (includeNumbers && this.depots) {
                label += ` (${this.depots.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.includeInputTypes) {
            let label = this.translationService.t('autocomplete.InputTypes');
            if (includeNumbers && this.groups) {
                label += ` (${this.groups.length})`;
            }

            whatsIncluded.push(label);
        }

        if (this.querySource && this.querySourceLabel) {
            whatsIncluded.push(`${this.querySourceLabel}`);
        }

        if (whatsIncluded.length > 1 && includeNumbers) {
            return this.getWhatsIncluded(false);
        }

        return whatsIncluded;
    }

    private populateMatches(matches: IAutocompleteMatch[][]) {

        while (this.matches.length < this.maxMatches || this.maxMatches === 0) {
            let numAdded = 0;
            for (let i = 0; i < matches.length; i++) {
                if (this.addMatch(matches[i])) {
                    numAdded++;
                }
            }

            if (numAdded === 0) {
                break;
            }
        }
    }

    private addMatch(matchSource: IAutocompleteMatch[]): boolean {
        if (matchSource.length > 0 && (this.matches.length < this.maxMatches || this.maxMatches === 0) && matchSource[0].label) {
            this.matches.push(matchSource[0]);
            matchSource.splice(0, 1);

            return true;
        }

        return false;
    }

    private findDriverMatches(lowercaseSearchText: string): IAutocompleteMatch[] {
        const driverMatches: IAutocompleteMatch[] = [];

        if (!this.includeDrivers) {
            return driverMatches;
        }

        if (this.drivers) {
            for (let i = 0; i < this.drivers.length; i++) {
                const driver = this.drivers[i];

                if (driver.Name.length > 0 && this.matchSearchText(driver.Name, lowercaseSearchText)) {
                    driverMatches.push(this.createDriverMatch(driver));
                }
            }
        }

        return driverMatches;
    }

    private findGeofenceMatches(lowercaseSearchText: string): IAutocompleteMatch[] {
        const geofenceMatches: IAutocompleteMatch[] = [];

        if (!this.includeGeofences) {
            return geofenceMatches;
        }

        if (this.geofences) {
            for (let i = 0; i < this.geofences.length; i++) {
                const geofence = this.geofences[i];

                if (geofence.Description && this.matchSearchText(geofence.Description, lowercaseSearchText)) {
                    if (geofence.GeofenceType === GeofenceType.Polygon && geofence.Points?.length > 0) {
                        this.geofenceService.setPolygonGeofenceVisualCenter(geofence);
                    }

                    geofenceMatches.push(this.createGeofenceMatch(geofence));
                }
            };
        }

        return geofenceMatches;
    }

    private findZoneMatches(lowercaseSearchText: string): IAutocompleteMatch[] {
        const zoneMatches: IAutocompleteMatch[] = [];

        if (!this.includeZones) {
            return zoneMatches;
        }

        if (this.zones) {
            for (let i = 0; i < this.zones.length; i++) {
                const zone = this.zones[i];

                if (zone.Description && this.matchSearchText(zone.Description, lowercaseSearchText)) {
                    if (zone.GeofenceType === GeofenceType.Polygon && zone.Points?.length > 0) {
                        this.geofenceService.setPolygonGeofenceVisualCenter(zone);
                    }

                    zoneMatches.push(this.createZoneMatch(zone));
                }
            };
        }

        return zoneMatches;
    }

    private findVehicleMatches(lowercaseSearchText: string): IAutocompleteMatch[] {

        const vehicleMatches: IAutocompleteMatch[] = [];

        if (!this.includeVehicles) {
            return vehicleMatches;
        }

        if (this.vehicles) {
            for (let i = 0; i < this.vehicles.length; i++) {
                const vehicle = this.vehicles[i];

                if (/*vehicle.Plot && */this.matchSearchText(vehicle.Reg, lowercaseSearchText)) {

                    vehicleMatches.push(this.createVehicleMatch(vehicle));
                }
            }
        }

        return vehicleMatches;
    }

    private findGroupMatches(lowercaseSearchText: string): IAutocompleteMatch[] {
        const matches: IAutocompleteMatch[] = [];

        if (!this.includeGroups) {
            return matches;
        }

        if (this.groups) {
            for (let i = 0; i < this.groups.length; i++) {
                const group = this.groups[i];

                if (group && this.matchSearchText(group.Name, lowercaseSearchText)) {

                    matches.push(this.createGroupMatch(group));
                }
            }
        }

        return matches;
    }

    private findUserMatches(lowercaseSearchText: string): IAutocompleteMatch[] {
        const matches: IAutocompleteMatch[] = [];

        if (!this.includeUsers) {
            return matches;
        }

        if (this.users) {
            for (let i = 0; i < this.users.length; i++) {
                const user = this.users[i];

                if (user && user.CurrentAccountUserMapping.Name && this.matchSearchText(user.CurrentAccountUserMapping.Name, lowercaseSearchText)) {

                    matches.push(this.createUserMatch(user));
                }
            }
        }

        return matches;
    }

    private findDepotMatches(lowercaseSearchText: string): IAutocompleteMatch[] {
        const matches: IAutocompleteMatch[] = [];

        if (!this.includeDepots) {
            return matches;
        }

        if (this.depots) {
            for (let i = 0; i < this.depots.length; i++) {
                const depot = this.depots[i];

                if (depot && this.matchSearchText(depot.Name, lowercaseSearchText)) {

                    matches.push(this.createDepotMatch(depot));
                }
            }
        }

        return matches;
    }

    private findInputTypeMatches(lowercaseSearchText: string): IAutocompleteMatch[] {
        const matches: IAutocompleteMatch[] = [];

        if (!this.includeInputTypes) {
            return matches;
        }

        if (this.inputTypes) {
            for (let i = 0; i < this.inputTypes.length; i++) {
                const inputType = this.inputTypes[i];

                if (inputType && this.matchSearchText(inputType.Name, lowercaseSearchText)) {

                    matches.push(this.createInputTypeMatch(inputType));
                }
            }
        }

        return matches;
    }

    private findMapzenPlaceMatches(lowercaseSearchText: string, mapzenAutocompleteResponse: IAutocompleteResponse): IAutocompleteMatch[] {

        if (!mapzenAutocompleteResponse) {
            console.warn("No autocomplete results");
            return [];
        }

        var transformedResults: IAutocompleteMatch[] = [];

        mapzenAutocompleteResponse.results.forEach(result => {
            transformedResults.push({
                label: result.formatted,
                object: result.geometry,
                location: { lat: result.geometry.lat, lng: result.geometry.lng },
                icon: "location_on",
                source: "Place"
            });
        });

        return transformedResults;
    }

    private findQuerySourceMatches(lowercaseSearchText: string, matches: IAutocompleteMatch[]): IAutocompleteMatch[] {
        const transformedResults: IAutocompleteMatch[] = [];

        if (matches) {
            matches.forEach(match => {
                if (this.matchSearchText(match.label, lowercaseSearchText)) {
                    transformedResults.push(match);
                }
            });
        }

        return transformedResults;
    }

    private matchSearchText(inputString: string, searchText: string) {
        if (!inputString || !searchText) {
            return true;
        }

        return inputString.toLocaleLowerCase().indexOf(searchText.toLocaleLowerCase()) >= 0;
    }

    private findGooglePlaceMatches(lowercaseSearchText: string, placeResults: google.maps.places.PlaceResult[]): IAutocompleteMatch[] {
        const placeMatches: IAutocompleteMatch[] = [];

        if (!this.includePlaces) {
            return placeMatches;
        }

        // Place i.e. business results
        for (let i = 0; i < placeResults.length; i++) {
            const result = placeResults[i];

            let matchLabel = `${result.formatted_address}`;

            if (result.types.indexOf("establishment") >= 0 || result.types.indexOf("point_of_interest") >= 0) {
                matchLabel = `${result.name} - ${result.formatted_address}`;
            }

            placeMatches.push({
                location: { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() },
                label: matchLabel,
                icon: "location_on",
                source: "Place"
            });
        }

        return placeMatches;
    }

    private createDriverMatch(driver: IDriver): IAutocompleteMatch {
        return {
            label: driver.DisplayName,
            objectId: driver.Id,
            icon: "person",
            source: "Driver",
            object: driver
        };
    }

    private createGeofenceMatch(geofence: IGeofence): IAutocompleteMatch {
        return {
            label: geofence.Description,
            objectId: geofence.Id,
            object: geofence,
            location: { lat: geofence.Latitude, lng: geofence.Longitude },
            icon: "filter_tilt_shift",
            source: "Geofence"
        };
    }

    private createZoneMatch(zone: IGeofence): IAutocompleteMatch {
        return {
            label: zone.Description,
            objectId: zone.Id,
            object: zone,
            location: { lat: zone.Latitude, lng: zone.Longitude },
            icon: "filter_tilt_shift",
            source: "Zone"
        };
    }

    private createVehicleMatch(vehicle: IVehicle): IAutocompleteMatch {
        return {
            label: vehicle.ShortReg,
            icon: "directions_car",
            objectId: vehicle.Id,
            object: vehicle,
            location: vehicle.Plot ? { lat: vehicle.Plot.Latitude, lng: vehicle.Plot.Longitude } : null,
            source: "Vehicle"
        };
    }

    private createGroupMatch(group: IGroup): IAutocompleteMatch {
        return {
            label: group.Name,
            icon: "group",
            objectId: group.Id,
            object: group,
            source: "Group"
        };
    }

    private createUserMatch(user: IUser): IAutocompleteMatch {
        return {
            label: user.CurrentAccountUserMapping.Name,
            icon: "account_box",
            objectId: user.Id,
            object: user,
            source: "User"
        };
    }

    private createDepotMatch(depot: IGroup): IAutocompleteMatch {
        return {
            label: depot.Name,
            icon: "home",
            objectId: depot.Id,
            object: depot,
            source: "Depot"
        };
    }


    private createInputTypeMatch(inputType: IInputType): IAutocompleteMatch {
        return {
            label: inputType.Name,
            icon: "sensors",
            objectId: inputType.Id,
            object: inputType,
            source: "Input Type"
        };
    }

    private setSelectedMatchById(matchId?: any): void {
        this.selectedMatch = null;

        if (matchId && !this.selectedMatchId) {
            this.selectedMatchId = matchId;
        }

        if (!this.selectedMatchId) {
            return;
        }

        if (this.includeGeofences) {
            this.geofences.forEach(geofence => {
                if (geofence.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createGeofenceMatch(geofence);
                }
            });
        }

        if (this.includeZones) {
            this.zones.forEach(zone => {
                if (zone.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createZoneMatch(zone);
                }
            });
        }

        if (this.includeGroups) {
            this.groups.forEach(group => {
                if (group.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createGroupMatch(group);
                }
            });
        }

        if (this.includeDepots) {
            this.depots.forEach(depot => {
                if (depot.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createDepotMatch(depot);
                }
            });
        }

        if (this.includeUsers) {
            this.users.forEach(user => {
                if (user.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createUserMatch(user);
                }
            });
        }

        if (this.includeDrivers) {
            this.drivers.forEach(driver => {
                if (driver.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createDriverMatch(driver);
                }
            });
        }

        if (this.includeVehicles) {
            this.vehicles.forEach(vehicle => {
                if (vehicle.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createVehicleMatch(vehicle);
                }
            });
        }

        if (this.includeInputTypes) {
            this.inputTypes.forEach(inputType => {
                if (inputType.Id === this.selectedMatchId) {
                    this.selectedMatch = this.createInputTypeMatch(inputType);
                }
            });
        }
    }

    private getData(forceRefresh = false): angular.IPromise<any> {
        // For now only update once, to mimick original behaviour
        if (this.dataLoadedPromise && !forceRefresh) {
            this.filterResourcesByGroup();
            this.buildPlaceHolderText();

            return this.dataLoadedPromise;
        }

        this.loading = true;

        const promiseArray: angular.IPromise<any>[] = [];

        if (this.includeGeofences || this.includeZones) {
            const p = this.geofenceService.getGeofences().then(geofences => {
                this.geofences = geofences.filter(geofence => geofence.Category === GeofenceCategory.Standard);
                this.zones = geofences.filter(geofence => geofence.Category === GeofenceCategory.Zone);
            });

            promiseArray.push(p);
        }

        if (this.includeVehicles) {
            const p = this.vehicleService.getVehicles({ includeLatestPlots: this.includeVehicleLatestPlot, dontCopy: true, includePeripheralMappings: true }).then(vehicles => {
                vehicles.forEach(vehicle => {
                    if (vehicle.vorRecord) {
                        vehicle.ShortReg = `[VOR] ${vehicle.ShortReg}`;
                    }
                });

                this.vehicles = vehicles;
            });

            promiseArray.push(p);
        }

        if (this.includeDrivers) {
            const p = this.driverService.getDrivers().then(drivers => {
                this.drivers = drivers;
            });

            promiseArray.push(p);
        }

        if (this.includeGroups || (this.filterByGroupId && this.filterByGroupId > 0)) {
            const p = this.groupService.getGroupsWithFullMappings().then(groups => {
                this.groups = groups;
            });

            promiseArray.push(p);
        }

        if (this.includeUsers) {
            const p = this.usersService.getUsers({
                includeMappings: false,
                includeAdmins: true
            }).then(users => {
                this.users = users.filter(user => user.CurrentAccountUserMapping?.Status === UserStatus.Active);
            });

            promiseArray.push(p);
        }

        if (this.includeDepots) {
            const p = this.groupService.getGroups().then(groups => {

                this.depots = groups.filter(group => group.IsDepot);
            });

            promiseArray.push(p);
        }

        if (this.includeInputTypes) {
            const p = this.inputTypeService.getInputTypes().then(inputTypes => {

                this.inputTypes = inputTypes;
            });

            promiseArray.push(p);
        }

        if (this.includePlaces) {
            var userOptions = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
            this.countryCode = userOptions.CountryCode.Value;
        }

        this.dataLoadedPromise = this.$q.all(promiseArray);

        this.dataLoadedPromise
            .then(() => {
                this.filterResourcesByGroup();
                this.buildPlaceHolderText();
                this.setSelectedMatchById();
            })
            .finally(() => {
                this.loading = false;

                // Disable auto complete
                var input = this.$element.find("input");
                input.attr("autocomplete", "no");
            })

        return this.dataLoadedPromise;
    }

    private filterResourcesByGroup() {
        if (!this.filterByGroupId || this.filterByGroupId < 0 || !this.groups) {
            return;
        }

        const groupDrivers: IDriver[] = [];
        const groupVehicles: IVehicle[] = [];
        const groupGeofences: IGeofence[] = [];
        const groupUsers: IUser[] = [];

        this.groups.forEach(group => {
            if (group.Id !== this.filterByGroupId) {
                return;
            }

            if (this.drivers) {
                group.DriverIds.forEach(driverId => {
                    this.drivers.forEach(driver => {
                        if (driver.Id === driverId) {
                            groupDrivers.push(driver);
                        }
                    });
                });
            }

            if (this.vehicles) {
                group.VehicleIds.forEach(vehicleId => {
                    this.vehicles.forEach(vehicle => {
                        if (vehicle.Id === vehicleId) {
                            groupVehicles.push(vehicle);
                        }
                    });
                });
            }

            if (this.geofences) {
                group.GeofenceIds.forEach(geofenceId => {
                    this.geofences.forEach(geofence => {
                        if (geofence.Id === geofenceId) {
                            groupGeofences.push(geofence);
                        }
                    });
                });
            }

            if (this.users) {
                group.userIds?.forEach(userId => {
                    this.users.forEach(user => {
                        if (user.Id === userId) {
                            groupUsers.push(user);
                        }
                    });
                });
            }
        });

        this.drivers = groupDrivers;
        this.vehicles = groupVehicles;
        this.geofences = groupGeofences;
        this.users = groupUsers;
    }
}

export class AutocompleteComponent implements angular.IComponentOptions {
    bindings: any;
    template: string;
    controller: any;
    require: { [index: string]: string; };

    constructor() {
        this.bindings = {
            label: "<",

            includeDrivers: "<",
            includeGeofences: "<",
            includeZones: "<",
            includeVehicles: "<",
            includePlaces: "<",
            includeGroups: "<",
            includeUsers: "<",
            includeDepots: "<",
            includeInputTypes: "<",

            includeVehicleLatestPlot: "<?",

            maxMatches: "@",
            searchOnClick: "@",
            customPlaceHolderText: "@",
            querySourceLabel: "@",
            isRequired: "<",
            filterByGroupId: "<",
            inputName: "<",

            selectedMatch: "<",
            selectedMatchId: "<",

            disabled: "<disableInput",
            eagerLoad: "<",

            querySource: "&",
            onSelect: "&",
            onSearchTextChanged: "&",
            onClear: "&"
        };

        this.require = { ngModel: "?ngModel" };

        this.controller = AutocompleteComponentController;
        this.template = require("./autocomplete.html");
    }
}
